/**
 * Monitoring Utilities - Sentry/LogRocket integration (V4 Architecture)
 * @module utils/monitoring
 *
 * FLOW[24270]: MS206 - Stubbed out until Sentry/LogRocket packages are installed
 *
 * This module provides error tracking and session recording integrations.
 * The actual Sentry/LogRocket SDKs should be installed when needed:
 *   npm install @sentry/react @sentry/tracing
 *   npm install logrocket logrocket-react
 */

// ============================================================================
// TYPES
// ============================================================================

export interface ErrorContext {
  userId?: string;
  userEmail?: string;
  userRole?: string;
  componentName?: string;
  action?: string;
  url?: string;
  extra?: Record<string, unknown>;
}

export interface BreadcrumbData {
  category: string;
  message: string;
  level: 'debug' | 'info' | 'warning' | 'error';
  data?: Record<string, unknown>;
}

export interface MonitoringConfig {
  dsn?: string;
  environment: 'development' | 'staging' | 'production';
  release?: string;
  tracesSampleRate?: number;
  enabled?: boolean;
}

export interface UserContext {
  id: string;
  email?: string;
  role?: string;
  name?: string;
}

export interface LogRocketConfig {
  appId?: string;
  enabled?: boolean;
  release?: string;
  dom?: {
    inputSanitizer?: boolean;
    textSanitizer?: boolean;
  };
}

export interface MonitoringOptions {
  sentry?: MonitoringConfig;
  logRocket?: LogRocketConfig;
}

// ============================================================================
// STUB IMPLEMENTATIONS (packages not installed)
// ============================================================================

/**
 * Initialize Sentry error tracking (STUB - package not installed)
 */
export async function initSentry(_config: MonitoringConfig): Promise<boolean> {
  console.info('[Monitoring] Sentry not installed. Install with: npm install @sentry/react');
  return false;
}

/**
 * Capture an error (logs to console when Sentry not installed)
 */
export function captureError(error: Error, context?: ErrorContext): void {
  console.error('[Error]', error, context);
}

/**
 * Capture a message (logs to console when Sentry not installed)
 */
export function captureMessage(
  message: string,
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
  context?: ErrorContext
): void {
  console.log(`[${level.toUpperCase()}]`, message, context);
}

/**
 * Add a breadcrumb (no-op when Sentry not installed)
 */
export function addBreadcrumb(_data: BreadcrumbData): void {
  // No-op when Sentry not installed
}

/**
 * Set user context (no-op when Sentry not installed)
 */
export function setSentryUser(_user: UserContext | null): void {
  // No-op when Sentry not installed
}

/**
 * Set custom tag (no-op when Sentry not installed)
 */
export function setSentryTag(_key: string, _value: string): void {
  // No-op when Sentry not installed
}

/**
 * Initialize LogRocket (STUB - package not installed)
 */
export async function initLogRocket(_config: LogRocketConfig): Promise<boolean> {
  console.info('[Monitoring] LogRocket not installed. Install with: npm install logrocket');
  return false;
}

/**
 * Identify user in LogRocket (no-op when not installed)
 */
export function identifyLogRocketUser(_user: UserContext): void {
  // No-op when LogRocket not installed
}

/**
 * Track custom event in LogRocket (no-op when not installed)
 */
export function trackLogRocketEvent(_eventName: string, _properties?: Record<string, unknown>): void {
  // No-op when LogRocket not installed
}

/**
 * Get LogRocket session URL (no-op when not installed)
 */
export function getLogRocketSessionURL(_callback: (url: string) => void): void {
  // No-op when LogRocket not installed
}

/**
 * Initialize all monitoring services (returns false for both)
 */
export async function initMonitoring(_options: MonitoringOptions): Promise<{
  sentry: boolean;
  logRocket: boolean;
}> {
  return { sentry: false, logRocket: false };
}

/**
 * Set user context in all monitoring services (no-op)
 */
export function setMonitoringUser(_user: UserContext | null): void {
  // No-op when monitoring not installed
}

/**
 * Track an event across all monitoring services (no-op)
 */
export function trackEvent(_eventName: string, _properties?: Record<string, unknown>): void {
  // No-op when monitoring not installed
}

/**
 * Report error to all monitoring services (logs to console)
 */
export function reportError(error: Error, context?: ErrorContext): void {
  console.error('[Error]', error, context);
}

/**
 * Start a performance transaction (returns null when not installed)
 */
export function startTransaction(
  _name: string,
  _operation: string
): { finish: () => void } | null {
  return null;
}

/**
 * Measure and report a performance metric (logs to console)
 */
export function measurePerformance(
  name: string,
  startTime: number,
  endTime: number = performance.now()
): void {
  const duration = endTime - startTime;
  console.debug(`[Performance] ${name}: ${duration.toFixed(2)}ms`);
}

/**
 * Check if Sentry is initialized (always false when not installed)
 */
export function isSentryEnabled(): boolean {
  return false;
}

/**
 * Check if LogRocket is initialized (always false when not installed)
 */
export function isLogRocketEnabled(): boolean {
  return false;
}

/**
 * Check if any monitoring is enabled (always false when not installed)
 */
export function isMonitoringEnabled(): boolean {
  return false;
}
